/**
 * Google Play Billing adapter (Digital Goods API + Payment Request API).
 *
 * Only works inside the Android Trusted Web Activity (Chrome 101+ with the
 * android-browser-helper billing bridge). Everywhere else `isAvailable()` is
 * false and callers must fall back to the non-purchase UI.
 *
 * SECURITY: this module never decides entitlement. A purchase token returned
 * here is an untrusted hint that MUST be verified by the backend
 * (POST /api/billing/google/verify) before Premium is unlocked.
 */

export const PLAY_BILLING_METHOD = "https://play.google.com/billing";

export const PRODUCT_IDS = Object.freeze({
  monthly: "subzero_premium_monthly",
  yearly: "subzero_premium_yearly",
});

export const ALL_PRODUCT_IDS = Object.freeze([PRODUCT_IDS.monthly, PRODUCT_IDS.yearly]);

// Result status vocabulary shared with the UI
export const BILLING_STATUS = Object.freeze({
  LOADING: "loading",
  READY: "ready",
  UNAVAILABLE: "unavailable",
  CANCELLED: "cancelled",
  ERROR: "error",
  SUCCESS: "success",
});

/** True when the Digital Goods API entry point exists (TWA / supported Chrome). */
export function isDigitalGoodsSupported() {
  return typeof window !== "undefined" && "getDigitalGoodsService" in window && typeof window.PaymentRequest === "function";
}

let servicePromise = null;

/**
 * Connect to the Play Billing service. Resolves to the service or null when
 * unavailable (unsupported browser, not launched from Play, bridge missing).
 */
export async function getPlayBillingService() {
  if (!isDigitalGoodsSupported()) return null;
  if (!servicePromise) {
    servicePromise = window
      .getDigitalGoodsService(PLAY_BILLING_METHOD)
      .catch(() => null);
  }
  const service = await servicePromise;
  if (!service) servicePromise = null; // allow a later retry
  return service;
}

/**
 * Fetch localized product details from Google Play.
 * @returns {Promise<{status: string, items?: Record<string, ItemDetails>, error?: string}>}
 * ItemDetails: { itemId, title, description, price: {currency, value}, subscriptionPeriod? }
 */
export async function getProductDetails(productIds = ALL_PRODUCT_IDS) {
  const service = await getPlayBillingService();
  if (!service) return { status: BILLING_STATUS.UNAVAILABLE };
  try {
    const details = await service.getDetails([...productIds]);
    const items = {};
    for (const d of details || []) {
      if (d && d.itemId) items[d.itemId] = d;
    }
    if (Object.keys(items).length === 0) {
      return { status: BILLING_STATUS.ERROR, error: "no_products" };
    }
    return { status: BILLING_STATUS.READY, items };
  } catch (e) {
    return { status: BILLING_STATUS.ERROR, error: e?.name || "details_failed" };
  }
}

/**
 * Format a Play price ({currency, value}) for display in the given locale.
 * Never used to invent prices; only formats what Google Play returned.
 */
export function formatPlayPrice(price, locale = "pl-PL") {
  if (!price || price.value == null || !price.currency) return "";
  const num = Number(price.value);
  if (Number.isNaN(num)) return `${price.value} ${price.currency}`;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: price.currency }).format(num);
  } catch {
    return `${num.toFixed(2)} ${price.currency}`;
  }
}

/**
 * Launch the Google Play purchase sheet for ONE product id.
 * Resolves with:
 *   { status: "success", purchaseToken, productId, complete(result) }
 *   { status: "cancelled" }            user dismissed the sheet
 *   { status: "unavailable" }          API not present
 *   { status: "error", error }         anything else
 *
 * `complete(result)` MUST be called by the caller after backend verification:
 *   complete("success") when the backend confirmed entitlement,
 *   complete("fail") otherwise.
 */
export async function purchase(productId, { currency = "PLN" } = {}) {
  if (!ALL_PRODUCT_IDS.includes(productId)) {
    return { status: BILLING_STATUS.ERROR, error: "unknown_product" };
  }
  if (!isDigitalGoodsSupported()) return { status: BILLING_STATUS.UNAVAILABLE };
  const service = await getPlayBillingService();
  if (!service) return { status: BILLING_STATUS.UNAVAILABLE };

  let request;
  try {
    request = new window.PaymentRequest(
      [{ supportedMethods: PLAY_BILLING_METHOD, data: { sku: productId } }],
      // Google Play ignores this total (price comes from the Play catalog) but
      // PaymentRequest requires a well-formed details object.
      { total: { label: "Total", amount: { currency, value: "0" } } }
    );
  } catch (e) {
    return { status: BILLING_STATUS.ERROR, error: e?.name || "request_init_failed" };
  }

  let response;
  try {
    response = await request.show();
  } catch (e) {
    if (e && (e.name === "AbortError" || e.name === "NotAllowedError")) {
      return { status: BILLING_STATUS.CANCELLED };
    }
    return { status: BILLING_STATUS.ERROR, error: e?.name || "show_failed" };
  }

  let details = response?.details;
  if (typeof details === "string") {
    try { details = JSON.parse(details); } catch { details = {}; }
  }
  const purchaseToken = details?.purchaseToken || details?.token;
  if (!purchaseToken) {
    try { await response.complete("fail"); } catch {}
    return { status: BILLING_STATUS.ERROR, error: "missing_purchase_token" };
  }

  return {
    status: BILLING_STATUS.SUCCESS,
    purchaseToken,
    productId,
    complete: async (result) => {
      try { await response.complete(result === "success" ? "success" : "fail"); } catch {}
    },
  };
}

/**
 * List purchases the Play account currently owns for this app.
 * @returns {Promise<{status: string, purchases?: Array<{itemId: string, purchaseToken: string}>, error?: string}>}
 */
export async function listPurchases() {
  const service = await getPlayBillingService();
  if (!service) return { status: BILLING_STATUS.UNAVAILABLE };
  try {
    const list = await service.listPurchases();
    const purchases = (list || [])
      .filter((p) => p && p.purchaseToken && ALL_PRODUCT_IDS.includes(p.itemId))
      .map((p) => ({ itemId: p.itemId, purchaseToken: p.purchaseToken }));
    return { status: BILLING_STATUS.READY, purchases };
  } catch (e) {
    return { status: BILLING_STATUS.ERROR, error: e?.name || "list_failed" };
  }
}
